






































import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, ref, set } from "@vue/composition-api";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { useBecomeCreatorState } from "@/hooks/useBecomeCreatorState";
import useFullCreatorApplicationUpload, {
  FullCreatorApplicationUploadStatus,
} from "@/hooks/useFullCreatorApplicationUpload";
import providers from "@/lib/api";
import { AttachmentFile } from "@/types";
import { AddingFilesOptions } from "@/components/PostCreate/types";
import AddMainVideo from "@/components/PostCreate/views/MediaPostCreateView/components/AddMainVideo.vue";
import VideoPostCard from "./VideoPostCard.vue";

const MIN_REQUIRED_VIDEOS = 3;

interface PendingFiles {
  files: FileList | File[] | AttachmentFile[];
  options: AddingFilesOptions;
  fromVault: boolean;
}

interface CardEntry {
  postId: string;
  pendingFiles: PendingFiles | null;
}

export default defineComponent({
  name: "VideosForm",
  components: {
    AddMainVideo,
    VideoPostCard,
  },
  setup(_, { emit }) {
    const instance = getCurrentInstance();
    const { authUser } = useAuthUser();
    const { update: updateBecomeCreatorState } = useBecomeCreatorState();
    const {
      activePostIds,
      videoControllerGeneration,
      discardVideoPost,
      isPostPublishing,
      startVideoPublishing,
      status,
    } = useFullCreatorApplicationUpload();
    const userId = computed(() => authUser.value?.id ?? "");

    const cards = ref<CardEntry[]>([]);
    const expandedPostId = ref<string | null>(null);
    const cardValidity = ref<Record<string, boolean>>({});
    const processing = ref(false);

    const isValid = computed(() => Object.values(cardValidity.value).filter(Boolean).length >= MIN_REQUIRED_VIDEOS);

    const onCardValidChange = (postId: string, valid: boolean) => {
      set(cardValidity.value, postId, valid);
      emit("setValid", isValid.value);
    };

    const onCardRemove = (postId: string) => {
      discardVideoPost(postId);
      cards.value = cards.value.filter(c => c.postId !== postId);
      const { [postId]: _, ...rest } = cardValidity.value;
      cardValidity.value = rest;
      if (expandedPostId.value === postId) {
        expandedPostId.value = cards.value.length > 0 ? cards.value[0].postId : null;
      }
      emit("setValid", isValid.value);
    };

    const onPendingFilesConsumed = (postId: string) => {
      const card = cards.value.find(card => card.postId === postId);
      if (card) {
        card.pendingFiles = null;
      }
    };

    const createPostAndAddCard = async (pendingFiles: PendingFiles) => {
      const post = await providers.ContentSetProvider.create({
        data: { type: ContentFeedTypeEnum.MEDIA, userId: userId.value },
      });
      cards.value.unshift({ postId: post.id, pendingFiles });
      expandedPostId.value = post.id;
      set(cardValidity.value, post.id, false);
      emit("setValid", isValid.value);
    };

    const onAddMainVideo = async (files: FileList | File[], options: AddingFilesOptions) => {
      await createPostAndAddCard({ files, options, fromVault: false });
    };

    const onAddMainVideoFromVault = async (files: AttachmentFile[], options: AddingFilesOptions) => {
      await createPostAndAddCard({ files, options, fromVault: true });
    };

    const getCardComponent = (postId: string): any => {
      if (!instance?.$refs) return null;
      const refs = instance.$refs[`card_${postId}`];
      return Array.isArray(refs) ? refs[0] : refs;
    };

    const submitStep = async () => {
      try {
        processing.value = true;
        const validPostIds: string[] = [];
        const invalidPostPromises = [];
        for (const card of cards.value) {
          const validity = cardValidity.value[card.postId];
          const vm = getCardComponent(card.postId);
          if (!vm) continue;
          if (validity) {
            validPostIds.push(card.postId);
          } else {
            invalidPostPromises.push(vm.remove());
          }
        }
        await Promise.all(invalidPostPromises);
        startVideoPublishing(validPostIds, async () => {
          await updateBecomeCreatorState(5);
        });
      } finally {
        processing.value = false;
      }
    };

    const cleanupEmptyPosts = () => {
      for (const card of cards.value) {
        const vm = getCardComponent(card.postId);
        if (!vm) continue;
        if (vm.isPostEmpty && !isPostPublishing(card.postId)) {
          vm.remove();
        }
      }
    };

    onBeforeUnmount(cleanupEmptyPosts);

    const loadExistingPosts = async () => {
      try {
        emit("setLoading", true);
        const resp = await providers.ContentSetProvider.search({
          data: {
            userId: userId.value,
            types: [ContentFeedTypeEnum.MEDIA],
            sort: "ID,DESC",
          },
        });
        const backendPostIds = resp.result.map(i => i.id);
        const postIds = [
          FullCreatorApplicationUploadStatus.ERROR,
          FullCreatorApplicationUploadStatus.RETRYING,
        ].includes(status.value)
          ? [...activePostIds.value, ...backendPostIds.filter(postId => !activePostIds.value.includes(postId))]
          : backendPostIds;
        cards.value = postIds.map(postId => ({ postId, pendingFiles: null }));
        if (cards.value.length > 0) {
          expandedPostId.value = cards.value[0].postId;
        }
      } finally {
        emit("setLoading", false);
      }
    };

    emit("setValid", false);
    emit("setCb", submitStep);
    loadExistingPosts();

    return {
      userId,
      videoControllerGeneration,
      cards,
      expandedPostId,
      processing,
      onCardValidChange,
      onCardRemove,
      onPendingFilesConsumed,
      onAddMainVideo,
      onAddMainVideoFromVault,
    };
  },
});
